import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { getEarlyCancelWindowEnd } from '@fitness-force/models';
import type {
  AppointmentBookingV2Detail,
  ClassBookingV2Detail,
  ClientBookingV2Membership,
} from '@fitness-force/models';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { getTrackModeI18nKey } from '../../../../utils/booking-track-mode.util';

export type ClientBookingDetailKind = 'class' | 'appointment';

export type ClientBookingDetailData =
  | ClassBookingV2Detail
  | AppointmentBookingV2Detail
  | null;

export interface BookingDetailMetaItem {
  labelKey: string;
  value: string;
  valueKey?: string;
  tagSeverity?: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';
  testId: string;
}

@Component({
  selector: 'client-booking-detail-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    ButtonModule,
    TagModule,
    SkeletonModule,
    MessageModule,
    RippleModule,
    TranslateModule,
    DateTimeFormatPipe,
  ],
  templateUrl: './client-booking-detail-dialog.component.html',
  styleUrl: './client-booking-detail-dialog.component.scss',
})
export class ClientBookingDetailDialogComponent {
  readonly visible = input(false);
  readonly loading = input(false);
  readonly bookingKind = input<ClientBookingDetailKind>('class');
  readonly detail = input<ClientBookingDetailData>(null);

  readonly closed = output<void>();

  readonly showAllMemberships = signal(false);

  readonly selectedMembership = computed(() => {
    const memberships = this.detail()?.memberships;
    if (!memberships?.length) {
      return null;
    }
    return memberships.find((m) => m.is_selected) ?? null;
  });

  readonly otherMemberships = computed(() => {
    const memberships = this.detail()?.memberships ?? [];
    const selectedId = this.selectedMembership()?.id;
    return memberships.filter((m) => m.id !== selectedId);
  });

  readonly hasMultipleMemberships = computed(() => {
    return (this.detail()?.memberships?.length ?? 0) > 1;
  });

  readonly summaryTitle = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    if (this.isClassDetail(detail)) {
      return detail.class?.type_name_primary_language ?? '';
    }
    return detail.appointment?.type_name_primary_language ?? '';
  });

  readonly summaryStartDatetime = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return null;
    }
    if (this.isClassDetail(detail)) {
      return detail.class?.start_datetime ?? null;
    }
    return detail.appointment?.start_datetime ?? null;
  });

  readonly summaryCoachName = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    if (this.isClassDetail(detail)) {
      return detail.class?.coach_name ?? '';
    }
    return detail.appointment?.coach_name ?? '';
  });

  readonly summaryStatus = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    if (this.isClassDetail(detail)) {
      return String(detail.booking_status ?? '');
    }
    return String(detail.status ?? '');
  });

  readonly summaryStatusSeverity = computed(() => {
    return this.statusSeverity(this.summaryStatus());
  });

  readonly headerBadges = computed(() => {
    const detail = this.detail();
    if (!detail || !this.isClassDetail(detail)) {
      return [];
    }
    const badges: { labelKey: string; severity: 'info' | 'warn' | 'danger' }[] =
      [];
    if (detail.class_booking_is_waitlist) {
      badges.push({
        labelKey: 'CLIENT_BOOKING.V2.WAITLIST',
        severity: 'warn',
      });
    }
    if (detail.trial_id > 0) {
      badges.push({ labelKey: 'CLIENT_BOOKING.V2.TRIAL', severity: 'info' });
    }
    if (detail.class_booking_is_first_time_class) {
      badges.push({
        labelKey: 'CLIENT_BOOKING.V2.FIRST_TIME',
        severity: 'info',
      });
    }
    if (detail.class_booking_is_payment_required) {
      badges.push({
        labelKey: 'CLIENT_BOOKING.V2.PAYMENT_PENDING',
        severity: 'danger',
      });
    }
    return badges;
  });

  readonly earlyCancelEnd = computed(() => {
    const detail = this.detail();
    if (!detail || !this.isClassDetail(detail)) {
      return null;
    }
    return getEarlyCancelWindowEnd(
      detail.class?.start_datetime,
      detail.class?.booking_late_cancel_in_minutes,
    );
  });

  readonly metaItems = computed((): BookingDetailMetaItem[] => {
    const detail = this.detail();
    if (!detail) {
      return [];
    }

    const items: BookingDetailMetaItem[] = [];

    const trackKey = getTrackModeI18nKey(detail.created_track_mode);
    items.push({
      labelKey: 'CLIENT_BOOKING.V2.TRACK_MODE',
      value: '',
      valueKey: trackKey || 'CLIENT_BOOKING.V2.NONE',
      testId: 'booking-detail-track-mode',
    });

    const statusTrackKey = getTrackModeI18nKey(detail.status_updated_track_mode);
    items.push({
      labelKey: 'CLIENT_BOOKING.V2.STATUS_UPDATED_TRACK_MODE',
      value: '',
      valueKey: statusTrackKey || 'CLIENT_BOOKING.V2.NONE',
      testId: 'booking-detail-status-track-mode',
    });

    if (this.isClassDetail(detail)) {
      items.push({
        labelKey: 'CLIENT_BOOKING.V2.COMP_SESSION',
        value: '',
        valueKey: detail.class_booking_is_comp_session
          ? 'CLIENT_BOOKING.V2.YES'
          : 'CLIENT_BOOKING.V2.NO',
        tagSeverity: detail.class_booking_is_comp_session ? 'info' : 'secondary',
        testId: 'booking-detail-comp-session',
      });
    } else {
      items.push({
        labelKey: 'CLIENT_BOOKING.V2.LATE_CANCEL',
        value: '',
        valueKey: detail.is_late_cancel
          ? 'CLIENT_BOOKING.V2.YES'
          : 'CLIENT_BOOKING.V2.NO',
        tagSeverity: detail.is_late_cancel ? 'warn' : 'secondary',
        testId: 'booking-detail-late-cancel',
      });
    }

    const createdDate = this.isClassDetail(detail)
      ? detail.booking_created_date
      : detail.appointment_created_date;

    items.push({
      labelKey: 'CLIENT_BOOKING.V2.BOOKING_CREATED',
      value: createdDate,
      testId: 'booking-detail-created-date',
    });

    if (this.earlyCancelEnd()) {
      items.push({
        labelKey: 'CLIENT_BOOKING.V2.EARLY_CANCEL_CLOSES',
        value: this.earlyCancelEnd()!.toISOString(),
        testId: 'booking-detail-early-cancel',
      });
    }

    return items;
  });

  isClassDetail(
    detail: ClientBookingDetailData,
  ): detail is ClassBookingV2Detail {
    return detail != null && 'booking_id' in detail;
  }

  membershipPrimaryLabel(membership: ClientBookingV2Membership): string {
    return (
      membership.membership_name_pri_lang ||
      membership.subscription_primary_name ||
      membership.service_category_primary_name ||
      '—'
    );
  }

  membershipSecondaryLabel(membership: ClientBookingV2Membership): string | null {
    const primary = this.membershipPrimaryLabel(membership);
    const candidates = [
      membership.subscription_primary_name,
      membership.service_category_primary_name,
      membership.membership_name_sec_lang,
    ].filter((c): c is string => !!c && c !== primary);

    return candidates[0] ?? null;
  }

  membershipSessionsLabel(membership: ClientBookingV2Membership): string {
    return `${membership.available_sessions} / ${membership.number_of_sessions}`;
  }

  toggleMemberships(): void {
    this.showAllMemberships.update((v) => !v);
  }

  onHide(): void {
    this.showAllMemberships.set(false);
    this.closed.emit();
  }

  statusSeverity(
    status: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    const normalized = status?.toLowerCase() ?? '';
    if (normalized === 'attended' || normalized === 'booked') {
      return 'success';
    }
    if (normalized === 'waitlist') {
      return 'warn';
    }
    if (normalized === 'cancelled' || normalized === 'noshow') {
      return 'danger';
    }
    return 'secondary';
  }

  isDateMetaItem(item: BookingDetailMetaItem): boolean {
    return (
      item.testId === 'booking-detail-created-date' ||
      item.testId === 'booking-detail-early-cancel'
    );
  }
}
